import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@mui/material';
import { X as CloseIcon, Minus, Plus } from '@phosphor-icons/react';
import { POS_PRIMARY } from './stylesProductPercabang';

// Bottom-sheet pemilihan opsi produk (mis. Level Pedas, Es) sebelum masuk
// keranjang kasir - dipakai kalau produknya punya grup opsi.
export default function ProductOptionsDialogKasir({ open, onClose, product, onConfirm }) {
  const [selected, setSelected] = useState({});
  const [note, setNote] = useState('');
  const [quantity, setQuantity] = useState(1);

  useEffect(() => {
    if (open && product) {
      setSelected({});
      setNote('');
      setQuantity(1);
    }
  }, [open, product]);

  if (!product) return null;

  const groups = product.Barang?.BarangOpsiGrups || [];
  const missingRequired = groups.filter((g) => g.wajib && !selected[g.uuid]);
  const canSubmit = missingRequired.length === 0;

  const optionsPrice = groups.reduce((sum, g) => {
    const chosenUuid = selected[g.uuid];
    if (!chosenUuid) return sum;
    const opt = (g.BarangOpsiPilihans || []).find((o) => o.uuid === chosenUuid);
    return sum + (opt ? parseFloat(opt.hargatambahan) : 0);
  }, 0);
  const unitPrice = parseFloat(product.Barang.harga) + optionsPrice;
  const totalPrice = unitPrice * quantity;

  const handleConfirm = () => {
    if (!canSubmit) return;
    const selectedOptions = groups
      .map((g) => {
        const chosenUuid = selected[g.uuid];
        if (!chosenUuid) return null;
        const opt = (g.BarangOpsiPilihans || []).find((o) => o.uuid === chosenUuid);
        if (!opt) return null;
        return {
          optionUuid: opt.uuid,
          grupnama: g.nama,
          opsinama: opt.nama,
          hargatambahan: parseFloat(opt.hargatambahan),
        };
      })
      .filter(Boolean);
    onConfirm(product, { quantity, note, selectedOptions });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
        <Typography variant="subtitle1" fontWeight={700}>
          Custom Pesanan
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon weight="bold" size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" fontWeight={700}>
            {product.Barang.namabarang}
          </Typography>
          <Typography variant="subtitle1" fontWeight={700} sx={{ color: POS_PRIMARY }}>
            Rp {parseFloat(product.Barang.harga).toLocaleString('id-ID')}
          </Typography>
        </Box>

        {groups.map((group) => (
          <Box key={group.uuid}>
            <Divider />
            <Box sx={{ px: 3, py: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                  {group.nama}
                </Typography>
                {group.wajib && (
                  <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                    Harus dipilih
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary">
                  • Pilih 1
                </Typography>
              </Box>
              <RadioGroup
                value={selected[group.uuid] || ''}
                onChange={(e) => setSelected((prev) => ({ ...prev, [group.uuid]: e.target.value }))}
              >
                {(group.BarangOpsiPilihans || []).map((opt) => (
                  <Box
                    key={opt.uuid}
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}
                  >
                    <FormControlLabel value={opt.uuid} control={<Radio size="small" />} label={opt.nama} />
                    <Typography variant="body2" color="text.secondary">
                      {parseFloat(opt.hargatambahan) > 0
                        ? `+Rp ${parseFloat(opt.hargatambahan).toLocaleString('id-ID')}`
                        : 'Gratis'}
                    </Typography>
                  </Box>
                ))}
              </RadioGroup>
            </Box>
          </Box>
        ))}

        <Divider />
        <Box sx={{ px: 3, py: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
            Catatan
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Opsional
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={2}
            placeholder="Tulis permintaan khusus di sini, ya"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            size="small"
          />
        </Box>

        <Divider />
        <Box sx={{ px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Jumlah pembelian
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#f0f0f0', borderRadius: 2, p: 0.5 }}>
            <IconButton size="small" onClick={() => setQuantity((q) => Math.max(1, q - 1))} sx={{ color: POS_PRIMARY }}>
              <Minus weight="bold" size={16} />
            </IconButton>
            <Typography variant="body2" sx={{ mx: 1.5 }}>
              {quantity}
            </Typography>
            <IconButton size="small" onClick={() => setQuantity((q) => q + 1)} sx={{ color: POS_PRIMARY }}>
              <Plus weight="bold" size={16} />
            </IconButton>
          </Box>
        </Box>
      </DialogContent>

      <Box sx={{ p: 2 }}>
        <Button
          fullWidth
          variant="contained"
          disabled={!canSubmit}
          onClick={handleConfirm}
          sx={{ py: 1.5, borderRadius: 3, textTransform: 'none', fontWeight: 700, bgcolor: POS_PRIMARY }}
        >
          {canSubmit
            ? `Tambah Pesanan - Rp ${totalPrice.toLocaleString('id-ID')}`
            : 'Pilih addon dulu'}
        </Button>
      </Box>
    </Dialog>
  );
}
